import React, { useState } from "react"
import { Button } from "../../../ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../ui/tabs"
import { ArrowRight, Briefcase } from "lucide-react"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from "../../../ui/sheet"
import { Dialog, DialogContent } from "../../../ui/dialog"
import JobApplicationForm from "./job-application-form"

const JobListingSection = () => {
  const [activeTab, setActiveTab] = useState("all")
  const [selectedJob, setSelectedJob] = useState(null)
  const [isJobDetailsOpen, setIsJobDetailsOpen] = useState(false)
  const [isApplicationOpen, setIsApplicationOpen] = useState(false)

  const jobs = [
    {
      id: 3,
      title: "Growth Marketing Manager",
      department: "Marketing",
      location: "Hybrid (New York)",
      type: "Hybrid",
      category: "Marketing",
      description:
        "We're looking for a results-driven Growth Marketing Manager to join our marketing team. In this role, you'll develop and execute marketing strategies to drive user acquisition, engagement, and retention across all our channels.",
      requirements: [
        "5+ years of experience in growth marketing or performance marketing",
        "Strong analytical skills and experience with marketing analytics tools",
        "Experience with A/B testing and optimization methodologies",
        "Excellent project management and communication skills",
        "Experience with paid social media advertising"
      ],
      responsibilities: [
        "Develop and implement growth strategies across multiple channels",
        "Analyze campaign performance and make data-driven optimizations",
        "Work closely with creative teams to develop effective marketing assets",
        "Manage marketing budget and allocate resources efficiently",
        "Stay up-to-date with industry trends and best practices"
      ],
      benefits: [
        "Competitive salary and equity package",
        "Flexible work arrangements",
        "Health, dental, and vision insurance",
        "Generous PTO policy",
        "Professional development budget"
      ]
    },
    {
      id: 4,
      title: "Backend Engineer",
      department: "Engineering",
      location: "Remote (US)",
      type: "Remote",
      category: "Engineering",
      description:
        "We're seeking an experienced Backend Engineer to help build and scale our platform. You'll work on designing and implementing APIs, services, and database structures that power our applications.",
      requirements: [
        "3+ years of experience in backend development",
        "Proficiency in Node.js, Python, or similar languages",
        "Experience with RESTful API design and implementation",
        "Knowledge of database systems (SQL and NoSQL)",
        "Familiarity with cloud services (AWS, GCP, or Azure)"
      ],
      responsibilities: [
        "Design, build, and maintain efficient, reusable, and reliable code",
        "Implement security and data protection measures",
        "Optimize applications for maximum speed and scalability",
        "Collaborate with frontend engineers to integrate user-facing elements",
        "Participate in code reviews and mentor junior engineers"
      ],
      benefits: [
        "Competitive salary and equity package",
        "Remote-first work culture",
        "Health, dental, and vision insurance",
        "Home office setup allowance",
        "Flexible working hours"
      ]
    }
  ]

  const filteredJobs =
    activeTab === "all" ? jobs : jobs.filter(job => job.category === activeTab)

  const handleViewRole = job => {
    setSelectedJob(job)
    setIsJobDetailsOpen(true)
  }

  const handleApplyNow = () => {
    setIsJobDetailsOpen(false)
    setIsApplicationOpen(true)
  }

  // Get unique departments and positions for the form dropdowns
  const departments = Array.from(new Set(jobs.map(job => job.department)))
  const positions = Array.from(new Set(jobs.map(job => job.title)))

  return (
    <section id="jobs" className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center gap-2 bg-oye-light-purple px-4 py-2 rounded-full text-oye-purple mb-4">
            <Briefcase size={16} />
            <span className="font-medium">Open Positions</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-black">
            Ready to make an <span className="text-[#E563A4]">impact</span>?
          </h2>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto">
            We're always looking for passionate people to join our growing team.
            Find a role that excites you!
          </p>
        </div>

        <div className="mb-8">
          <Tabs
            defaultValue="all"
            className="w-full"
            onValueChange={setActiveTab}
          >
            <TabsList className="mb-8 w-full md:w-auto flex justify-center border border-solid rounded-full p-1 bg-white">
              <TabsTrigger
                value="all"
                className="rounded-full px-5 data-[state=active]:bg-oye-light-purple data-[state=active]:text-oye-purple"
              >
                All Roles
              </TabsTrigger>
              <TabsTrigger
                value="Engineering"
                className="rounded-full px-5 data-[state=active]:bg-oye-light-purple data-[state=active]:text-oye-purple"
              >
                Engineering
              </TabsTrigger>
              <TabsTrigger
                value="Marketing"
                className="rounded-full px-5 data-[state=active]:bg-oye-light-purple data-[state=active]:text-oye-purple"
              >
                Marketing
              </TabsTrigger>
              <TabsTrigger
                value="Operations"
                className="rounded-full px-5 data-[state=active]:bg-oye-light-purple data-[state=active]:text-oye-purple"
              >
                Operations
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-0">
              <div className="space-y-4">
                {filteredJobs.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">
                      No open positions in this category at the moment.
                    </p>
                  </div>
                ) : (
                  filteredJobs.map(job => (
                    <div
                      key={job.id}
                      className="flex flex-col md:flex-row md:items-center justify-between p-6 border border-solid border-gray-200 rounded-2xl hover:border-oye-purple/30 hover:shadow-md transition-all duration-300"
                    >
                      <div className="mb-4 md:mb-0">
                        <h3 className="text-xl font-semibold mb-2">
                          {job.title}
                        </h3>
                        <div className="flex flex-wrap gap-3">
                          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                            {job.department}
                          </span>
                          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                            {job.location}
                          </span>
                          <span className="text-sm text-oye-purple bg-oye-light-purple px-3 py-1 rounded-full">
                            {job.type}
                          </span>
                        </div>
                      </div>
                      <Button
                        className="bg-white text-oye-purple border border-solid border-oye-purple hover:bg-oye-light-purple flex items-center gap-2"
                        onClick={() => handleViewRole(job)}
                      >
                        View Role
                        <ArrowRight size={16} />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Job Details Sheet */}
      <Sheet open={isJobDetailsOpen} onOpenChange={setIsJobDetailsOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {selectedJob && (
            <>
              <SheetHeader className="mb-6">
                <SheetTitle className="text-2xl font-bold">
                  {selectedJob.title}
                </SheetTitle>
                <SheetDescription className="text-base text-gray-500">
                  {selectedJob.department} · {selectedJob.location}
                </SheetDescription>
              </SheetHeader>

              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">About the Role</h3>
                  <p className="text-gray-700">{selectedJob.description}</p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Requirements</h3>
                  <ul className="list-disc pl-5 space-y-2">
                    {selectedJob.requirements?.map((req, index) => (
                      <li key={index} className="text-gray-700">
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">
                    Responsibilities
                  </h3>
                  <ul className="list-disc pl-5 space-y-2">
                    {selectedJob.responsibilities?.map((resp, index) => (
                      <li key={index} className="text-gray-700">
                        {resp}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Benefits</h3>
                  <ul className="list-disc pl-5 space-y-2">
                    {selectedJob.benefits?.map((benefit, index) => (
                      <li key={index} className="text-gray-700">
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-4">
                  <Button
                    className="w-full bg-oye-purple hover:bg-oye-dark-purple text-white"
                    onClick={handleApplyNow}
                  >
                    Apply Now
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Job Application Dialog */}
      <Dialog open={isApplicationOpen} onOpenChange={setIsApplicationOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto p-0 sm:p-6">
          <JobApplicationForm
            onClose={() => setIsApplicationOpen(false)}
            position={selectedJob?.title || ""}
            departments={departments}
            positions={positions}
          />
        </DialogContent>
      </Dialog>
    </section>
  )
}

export default JobListingSection
